import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { CdkDragDrop, CdkDragEnd, CdkDragStart, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    normalizeSessionStatus,
    SESSION_STATUS,
} from '@app/core/constants/core.constants';
import { LoggingService } from '@app/core/services';
import {
    TABLE_BUILDER_DROPDOWN_FILTER_PLACEHOLDER,
    TABLE_BUILDER_DROPDOWN_FILTER_TYPE,
    TABLE_BUILDER_DROPDOWN_POPUP_HEIGHT,
    TABLE_BUILDER_DROPDOWN_QUERY,
} from '@modules/custom-table/components/table-builder/table-builder-dropdown.queries';
import { SortableColumnDirective, SortEvent } from '@modules/custom-table/directives';
import { CustomTableService } from '@modules/custom-table/services';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Component({
    selector: 'cwr-table-builder',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './table-builder.component.html',
    styleUrls: ['table-builder.component.scss'],
    providers: [CustomTableService],
})
export class TableBuilderComponent implements OnInit, OnChanges, OnDestroy {
    private static readonly amountFormatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    private static readonly amountLikeTokens = [
        'amount',
        'balance',
        'paid',
        'payment',
        'gross',
        'net',
        'total',
        'price',
        'cost',
        'tax',
        'vat',
        'discount',
        'debit',
        'credit',
        'line total',
    ];
    private readonly columnDisplayFormat = new Map<string, 'text' | 'amount'>();
    private dragSourceWidthBackup = '';
    private dragSourceMinWidthBackup = '';
    private dragSourceMaxWidthBackup = '';
    private dragSourceCellStyleBackup: Array<{
        element: HTMLElement;
        width: string;
        minWidth: string;
        maxWidth: string;
        boxSizing: string;
    }> = [];
    selectedRow: Record<string, any> | null = null;

    constructor(
        public mytableServices: CustomTableService,
        public changeDetectorRef: ChangeDetectorRef,
        private loggingService: LoggingService
    ) {}
    @Input() loading = false; // loading state

    /** Active page number. Used by server-side pagination consumers. */
    @Input() pageNumber = 1;
    @Input() pageSize = 8; // Number of rows per page
    /** Total row count provided by server-side pagination consumers. */
    @Input() externalTotalItems: number | null = null;
    /** Enables server-side pagination mode (disables local slicing in service). */
    @Input() serverSidePagination = false;
    @Input() pageSizeOptions: number[] = [8, 14, 22];
    @Input() table_columns: {
        key: string;
        label: string;
        sortable: boolean;
        displayFormat?: 'text' | 'amount';
    }[] = []; // Column configuration
    @Input() inputTableRowData: any[] = []; // Dynamic table data

    @Input() customCheckbox = false;
    /**
     * Optional header label for the custom checkbox column.
     * Set empty string to hide text and show only the checkbox.
     */
    @Input() customCheckboxHeaderLabel = 'Select All';
    /** Optional tooltip for the header checkbox. */
    @Input() customCheckboxHeaderTooltip = '';
    /** Optional tooltip for each row checkbox. */
    @Input() customCheckboxRowTooltip = '';
    /** Controls whether header checkbox shows indeterminate state. */
    @Input() customCheckboxUseIndeterminate = true;
    /**
     * When enabled, header uncheck restores only rows that were initially selected.
     */
    @Input() preserveInitialSelectionsOnHeaderClear = false;
    /**
     * Stable list of row ids that were selected from backend payload.
     * Used together with preserveInitialSelectionsOnHeaderClear.
     */
    @Input() initialSelectedRowIds: Array<string | number> | null = null;
    @Input() iconDeleteOperation = false;
    @Input() iconDownloadOperation = false;
    @Input() iconEditOperation = false;
    @Input() iconViewOperation = false;
    @Input() disableDownloadOperation = false;
    @Input() disableViewOperation = false;
    @Input() disableDeleteOperation = false;
    /**
     * Optional row-level predicates to control whether
     * view/download actions are disabled for a given row.
     * When provided, they are OR-ed with the global
     * disableDownloadOperation/disableViewOperation flags.
     */
    @Input() disableViewForRow?: (row: any) => boolean;
    @Input() disableDownloadForRow?: (row: any) => boolean;
    @Input() disableDeleteForRow?: (row: any) => boolean;
    @Input() disableOcrForRow?: (row: any) => boolean;
    @Input() disableToolOperation = false;
    @Input() disableToolForRow?: (row: any) => boolean;
    @Input() iconToolOperation = false;
    @Input() iconOcrProcOperation = false;
    /**
     * Optional header label override for the OCR/process action column.
     * When empty, the default repeat icon remains visible.
     */
    @Input() ocrOperationHeaderLabel = '';
    @Input() tableRowPadding = ''; // Default padding class
    @Input() tableHeaderTitle = '';
    /**
     * Optional external selected row identity.
     * When provided, row highlight is driven by row id instead of local index.
     * This keeps table UI selection aligned with shared/store-based active state.
     */
    @Input() selectedRowId: string | null = null;
    @Input() showSearch = true;
    @Input() searchPlaceholder = 'Full text search...';
    @Input() disableSearchInput = false;
    @Input() showPagination = true;
    @Input() tableNoteCount: number | null = null;
    @Input() tableNoteText = '';
    @Input() showStatusBadges = false;
    @Input() disableRowHover = false;
    @Input() disableRowSelection = false;
    @Input() searchOperator: 'contains' | 'startswith' | 'endswith' = 'contains';
    @Input() emptyStateMessage = 'No Data Found';
    @Input() enableSourceEdit = false;
    @Input() targetOptions: Array<string | { id: number; canonicalName: string }> = [];
    @Input() lockedTargetOptions: string[] = [];
    @Input() sourceOptions: string[] = [];
    @Input() mappingStatusOptions: string[] = [];
    /**
     * Opt-in searchable filtering for mapping dropdowns.
     * Defaults stay disabled so existing table-builder consumers keep current behavior.
     */
    @Input() enableSourceFiltering = false;
    @Input() enableTargetFiltering = false;
    /** Allows selecting the same source value across multiple rows for specific screens. */
    @Input() allowDuplicateSourceSelection = false;
    /** Allows selecting the same ERP target value across multiple rows for specific screens. */
    @Input() allowDuplicateTargetSelection = false;
    @Input() tableWidthPx?: number;
    /**
     * When true, applies tableWidthPx to wrapper containers as well.
     * Keep this enabled for legacy screens. Disable it for modal grids that
     * need horizontal overflow on the inner datatable container.
     */
    @Input() lockContainerWidthToTable = true;
    /**
     * When true, applies tableWidthPx directly on the <table> element.
     * Disable for modal grids that should size table width from content.
     */
    @Input() lockTableElementWidth = true;
    /** Optional row class applied to draggable rows; useful for modal-specific drag preview styling. */
    @Input() dragRowClass = '';
    /** Opt-in: lock drag preview width to source row width while dragging. */
    @Input() lockDragPreviewWidth = false;
    /**
     * Enables row drag-and-drop reordering for this table instance.
     * Opt-in by design to avoid changing behavior in existing screens.
     */
    @Input() enableRowReorder = false;
    /**
     * Shared dropdown field mapping for reconciliation/consolidation selectors.
     * `disabled` is mapped explicitly so already-used options retain the greyed-out
     * visual state even when Syncfusion re-renders the filtered popup list.
     */
    readonly targetDropdownFields = { text: 'text', value: 'value', disabled: 'isDisabled' };
    readonly dropdownFilterType = TABLE_BUILDER_DROPDOWN_FILTER_TYPE;
    readonly dropdownFilterPlaceholder = TABLE_BUILDER_DROPDOWN_FILTER_PLACEHOLDER;
    readonly dropdownPopupHeight = TABLE_BUILDER_DROPDOWN_POPUP_HEIGHT;
    readonly dropdownQuery = TABLE_BUILDER_DROPDOWN_QUERY;

    /**
     * Optional callback-style handlers used by pages that prefer
     * direct function calls over Output EventEmitters.
     * When these are provided, the corresponding EventEmitter is not used.
     */
    @Input() rowClickHandler?: (row: any) => void;
    @Input() ocrClickHandler?: (row: any) => void;
    @Input() viewClickHandler?: (row: any) => void;
    @Input() downloadClickHandler?: (row: any) => void;
    @Input() deleteClickHandler?: (row: any) => void;
    @Input() editClickHandler?: (row: any) => void;
    @Input() toolClickHandler?: (row: any) => void;

    @Output() selectedRowClicked = new EventEmitter<any>();
    @Output() selectedRowsChange = new EventEmitter<any[]>();
    @Output() selectSingleItemClick = new EventEmitter<any>();
    @Output() viewSingleItemClick = new EventEmitter<any>();
    @Output() ocrProcessClick = new EventEmitter<any>();
    @Output() deleteRowClicked = new EventEmitter<any>();
    @Output() editRowClicked = new EventEmitter<any>();
    @Output() downloadIconClicked = new EventEmitter<any>();
    @Output() toolSingleItemClick = new EventEmitter<any>();
    @Output() targetSelectionChange = new EventEmitter<{
        row: any;
        value: string;
    }>();
    @Output() targetSelectionLockedClick = new EventEmitter<any>();
    @Output() targetSelectionDuplicateClick = new EventEmitter<any>();
    @Output() sourceSelectionChange = new EventEmitter<{
        row: any;
        value: string;
    }>();
    @Output() sourceSelectionDuplicateClick = new EventEmitter<any>();
    @Output() mappingStatusSelectionChange = new EventEmitter<{
        row: any;
        value: string;
    }>();
    @Output() sourceEditClick = new EventEmitter<any>();
    @Output() sourceEditConfirm = new EventEmitter<any>();
    @Output() sourceEditCancel = new EventEmitter<any>();
    @Output() sourceEditHeaderClick = new EventEmitter<void>();
    @Output() deleteActionClick = new EventEmitter<any>();
    @Output() erpRuleChange = new EventEmitter<{ row: any; checked: boolean }>();
    @Output() newRuleConfirmClick = new EventEmitter<any>();
    @Output() editCancelClick = new EventEmitter<any>();
    @Output() reportFieldChange = new EventEmitter<any>();
    /**
     * Emits the entire reordered row list after a drag-and-drop operation.
     * Parent components can store this order in their own state/save payload.
     */
    @Output() rowOrderChanged = new EventEmitter<any[]>();
    /** Emits when user changes page number from table pager. */
    @Output() pageNumberChange = new EventEmitter<number>();
    /** Emits when user changes page size from table pager. */
    @Output() pageSizeChange = new EventEmitter<number>();
    /**
     * Emits the latest search term so parent components can coordinate
     * master/detail behavior with the table's local filtering state.
     */
    @Output() searchTermChange = new EventEmitter<string>();
    @Output() sortChange = new EventEmitter<{ column: string; direction: 'asc' | 'desc' }>();
    /**
     * Emits the current rendered page rows so parents can validate whether
     * the active selection is actually visible on screen.
     */
    @Output() currentPageRowsChange = new EventEmitter<any[]>();

    private tableDataSubject = new BehaviorSubject<any[]>([]);
    private readonly subscriptions = new Subscription();
    /**
     * Captures the user's current page before local search begins so clearing
     * the search can restore the same browsing context.
     */
    private restorePageAfterSearch = 1;

    myRowData$!: Observable<any[]>; // Changed to any[] to handle dynamic row data
    total$!: Observable<number>;
    sortedColumn!: string;
    sortedDirection!: string;
    loading$!: Observable<boolean>;
    selectedRows: any[] = []; // Track selected rows explicitly
    selectedRowItem: any | null = null;
    selectedSession: string | null = null;
    private initialSelectedIdSet = new Set<string>();
    // -1 means "no local selection yet" and avoids default highlighting row 1.
    selectedRowIndex = -1;

    @ViewChildren(SortableColumnDirective) headers!: QueryList<SortableColumnDirective>;

    paginationSummary$!: Observable<string>;

    get skeletonRows(): any[] {
        // Generate empty rows for skeleton placeholders
        return Array(this.pageSize).fill({});
    }

    /**
     * Single source of truth for the pager UI.
     * The component renders the same page value used by the table service
     * to slice rows and build pagination summaries.
     */
    get currentPage(): number {
        return this.mytableServices.page;
    }

    ngOnInit() {
        this.syncInitialSelectedSnapshot();
        this.refreshColumnDisplayFormat();
        // Add 'isSelected' dynamically if customCheckbox is true
        if (this.customCheckbox) {
            this.inputTableRowData = this.inputTableRowData.map((row) => ({
                ...row,
                selected: Object.prototype.hasOwnProperty.call(row, 'selected') ? row.selected : false,
            }));
        }

        this.mytableServices.tableRowItemsRaw = this.inputTableRowData;

        // redundant
        // this.myRowData$ = this.tableDataSubject.asObservable();
        this.tableDataSubject.next(this.inputTableRowData); // Initialize table data

        // Link observables from service to component properties
        this.myRowData$ = this.mytableServices.currentPageRows$;
        this.total$ = this.mytableServices.total$;
        this.loading$ = this.mytableServices.loading$;
        this.subscriptions.add(
            this.mytableServices.currentPageRows$.subscribe((rows) => {
                this.currentPageRowsChange.emit(rows);
            })
        );

        // Set initial page size from input
        this.mytableServices.page = this.pageNumber || 1;
        this.mytableServices.pageSize = this.pageSize;
        this.mytableServices.serverSidePagination = this.serverSidePagination;
        this.mytableServices.externalTotalItems = this.externalTotalItems;
        this.mytableServices.searchOperator = this.searchOperator;
        if (!this.showPagination) {
            this.mytableServices.pageSize = this.inputTableRowData.length || this.pageSize;
            this.mytableServices.page = 1;
        }

        // Initialize table data
        this.updateTableData();
        this.syncSelectionFromInputs();

        this.onDataLoaded(this.inputTableRowData);

        // Paganation Summary
        // Initialize the pagination summary
        this.paginationSummary$ = this.total$.pipe(
            map((total) => {
                const page = this.mytableServices.page;
                const pageSize = this.mytableServices.pageSize;
                const from = (page - 1) * pageSize + 1;
                let to = page * pageSize;
                if (to > total) to = total;
                return `Showing ${from} to ${to} of ${total} entries`;
            })
        );
    }

    /**
     * Click handler for data cells (non-icon columns).
     * If a custom rowClickHandler is provided, it is used instead of
     * emitting selectedRowClicked. This allows certain pages (like the
     * upload session view) to control master/detail behaviour without
     * relying on Output EventEmitters.
     */
    onDataCellClick(row: any, index: number): void {
        this.selectedRowItem = row;
        this.selectedSession = row.id;
        this.selectedRowIndex = index;

        if (this.rowClickHandler) {
            this.rowClickHandler(row);
            this.selectedRows = [row];
            return;
        }

        this.onRowItemClick(row, index);
    }

    onRowItemClick(row: any, index: number): void {
        this.selectedRowItem = row;
        this.selectedSession = row.id;

        // console.clear();
        this.loggingService.debug('Row clicked with id = ', row.id);

        this.selectedRows = [row]; // Track the selected row
        this.selectedRowClicked.emit(row); // Emit the selected row data

        this.selectedRowIndex = index;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    onDataLoaded(newData: any[]): void {
        // Reassign selectedRow if the same ID is in the new dataset
        const foundRow = newData.find((item) => item.id === this.selectedSession);
        if (foundRow) {
            this.selectedRowItem = foundRow;
        } else {
            this.selectedRowItem = null; // or handle if not found
            this.selectedRowIndex = -1;
        }
    }

    /**
     * Keeps local selection in sync with optional parent-provided selected row id.
     * We intentionally do not force a fallback row when the id is not present.
     */
    private syncSelectionFromInputs(): void {
        if (!this.selectedRowId) {
            this.selectedRowItem = null;
            this.selectedSession = null;
            this.selectedRowIndex = -1;
            this.selectedRows = [];
            return;
        }

        const selectedId = String(this.selectedRowId);
        const foundRow = this.inputTableRowData.find(
            (item) => String(item?.id ?? '') === selectedId
        );

        if (!foundRow) {
            this.selectedRowItem = null;
            this.selectedRowIndex = -1;
            this.loggingService.debug(
                '[TableBuilder] selectedRowId not found in current data set.',
                selectedId
            );
            return;
        }

        this.selectedRowItem = foundRow;
        this.selectedSession = selectedId;
    }

    /**
     * Returns true if the row should render as selected.
     * Priority:
     * 1) external selectedRowId
     * 2) local selected index (legacy behavior)
     */
    isRowSelected(row: any, index: number): boolean {
        if (this.disableRowSelection) {
            return false;
        }

        if (this.selectedRowId) {
            return String(row?.id ?? '') === String(this.selectedRowId);
        }

        return this.selectedRowIndex >= 0 && index === this.selectedRowIndex;
    }

    // Assigns cell classes based on header semantics and value type.
    getCellClasses(
        row: any,
        col: { key: string; label?: string; displayFormat?: 'text' | 'amount' }
    ): Record<string, boolean> {
        const isMissingValue = this.isMissingDisplayValue(row?.[col.key]);
        const label = (col?.label ?? col?.key ?? '').toString();
        const labelLower = label.toLowerCase();
        const isNumberText = this.isIdentifierLikeLabel(labelLower);
        const isCurrency = this.isAmountColumn(col);

        // Treat "number"/"no" columns as text identifiers (no numeric styling).
        if (isNumberText) {
            return { 'cell-missing-value': isMissingValue };
        }

        // Treat currency columns as numeric, even if the data arrives as strings.
        if (isCurrency) {
            const numericMeta = this.getNumericMeta(row?.[col.key]);
            return {
                amount: true,
                'amount-negative': numericMeta.isNegative,
                'cell-neutral': !numericMeta.isNegative,
                'cell-missing-value': isMissingValue,
            };
        }

        // Default to inferred numeric detection for all other columns.
        const numericMeta = this.getNumericMeta(row?.[col.key]);
        return {
            amount: numericMeta.isNumeric,
            'amount-negative': numericMeta.isNegative,
            'cell-neutral': numericMeta.isNumeric && !numericMeta.isNegative,
            'cell-missing-value': isMissingValue,
        };
    }

    /**
     * A reconciliation result cell is considered "missing" when backend sends
     * the literal placeholder text "MISSING".
     */
    private isMissingDisplayValue(value: unknown): boolean {
        return String(value ?? '').trim().toUpperCase() === 'MISSING';
    }

    /**
     * Returns display-safe cell text while preserving search highlight behavior.
     * Amount-like columns are formatted to 2 decimals.
     */
    getCellDisplayValue(
        row: any,
        col: { key: string; label?: string; displayFormat?: 'text' | 'amount' }
    ): string {
        const rawValue = row?.[col.key];
        if (rawValue === null || rawValue === undefined) {
            return '';
        }

        if (!this.isAmountColumn(col)) {
            return String(rawValue);
        }

        const parsedNumber = this.parseNumericValue(rawValue);
        if (parsedNumber === null) {
            return String(rawValue);
        }

        return TableBuilderComponent.amountFormatter.format(parsedNumber);
    }

    onTargetSelectChange(row: any, value: string): void {
        this.targetSelectionChange.emit({ row, value: value ?? '' });
    }

    isTargetOptionUsed(option: string | number, currentRow: any): boolean {
        if (!option) {
            return false;
        }
        return this.inputTableRowData.some(
            (row) => row !== currentRow && row?.target === option
        );
    }

    isSourceOptionUsed(option: string, currentRow: any): boolean {
        if (!option) {
            return false;
        }
        const normalizedOption = this.normalizeSourceOption(option);
        return this.inputTableRowData.some(
            (row) =>
                row !== currentRow &&
                this.normalizeSourceOption(row?.source ?? '') === normalizedOption
        );
    }

    isTargetOptionLocked(option: string | number): boolean {
        if (!option || this.lockedTargetOptions.length === 0) {
            return false;
        }
        return this.lockedTargetOptions.includes(String(option).toLowerCase());
    }

    private normalizeTargetOption(option: string | { id: number; canonicalName: string }): {
        text: string;
        value: string;
    } {
        if (typeof option === 'string') {
            return { text: option, value: option };
        }
        return { text: option.canonicalName, value: String(option.id) };
    }

    private isCurrentRowTargetOption(optionValue: string, currentRow: any): boolean {
        return String(currentRow?.target ?? '').trim() === String(optionValue ?? '').trim();
    }

    getRowTargetOptions(row: any): Array<{ text: string; value: string; isDisabled: boolean }> {
        return [
            { text: 'Select', value: '', isDisabled: false },
            ...(this.targetOptions || []).map((option) => {
                const normalized = this.normalizeTargetOption(option);
                const isCurrentRowValue = this.isCurrentRowTargetOption(normalized.value, row);
                return {
                    ...normalized,
                    isDisabled:
                        !this.allowDuplicateTargetSelection &&
                        !isCurrentRowValue &&
                        (
                            this.isTargetOptionUsed(normalized.value, row) ||
                            this.isTargetOptionLocked(normalized.value)
                        ),
                };
            }),
        ];
    }

    getRowSourceOptions(row: any): Array<{ text: string; value: string; isDisabled: boolean }> {
        return [
            { text: 'Select', value: '', isDisabled: false },
            ...(this.sourceOptions || []).map((option) => ({
                text: option,
                value: option,
                isDisabled: !this.allowDuplicateSourceSelection && this.isSourceOptionUsed(option, row),
            })),
        ];
    }

    private normalizeSourceOption(value: string): string {
        return (value ?? '').trim().toLowerCase();
    }

    // Strict numeric parsing to avoid treating IDs like "NC-2777" as numbers.
    private getNumericMeta(value: unknown): { isNumeric: boolean; isNegative: boolean } {
        if (value === null || value === undefined) {
            return { isNumeric: false, isNegative: false };
        }

        if (typeof value === 'number') {
            return { isNumeric: Number.isFinite(value), isNegative: value < 0 };
        }

        const raw = String(value).trim();
        if (!raw) {
            return { isNumeric: false, isNegative: false };
        }

        if (/[a-zA-Z]/.test(raw)) {
            return { isNumeric: false, isNegative: false };
        }

        let negative = false;
        let cleaned = raw;
        if (cleaned.startsWith('(') && cleaned.endsWith(')')) {
            negative = true;
            cleaned = cleaned.slice(1, -1);
        }

        cleaned = cleaned.replace(/,/g, '').trim();
        if (cleaned.startsWith('-')) {
            negative = true;
        }

        cleaned = cleaned.replace(/[^0-9.-]/g, '');
        if (!/^-?\d+(\.\d+)?$/.test(cleaned)) {
            return { isNumeric: false, isNegative: false };
        }

        const parsed = Number(cleaned);
        if (!Number.isFinite(parsed)) {
            return { isNumeric: false, isNegative: false };
        }

        return { isNumeric: true, isNegative: negative || parsed < 0 };
    }

    private parseNumericValue(value: unknown): number | null {
        const numericMeta = this.getNumericMeta(value);
        if (!numericMeta.isNumeric) {
            return null;
        }

        let cleaned = String(value).trim();
        const hasBrackets = cleaned.startsWith('(') && cleaned.endsWith(')');
        cleaned = cleaned.replace(/[(),]/g, '').trim();
        const parsed = Number(cleaned);
        if (!Number.isFinite(parsed)) {
            return null;
        }

        return hasBrackets ? -Math.abs(parsed) : parsed;
    }

    private isAmountColumn(col: {
        key: string;
        label?: string;
        displayFormat?: 'text' | 'amount';
    }): boolean {
        const normalizedKey = String(col?.key ?? '').toLowerCase();
        const normalizedLabel = String(col?.label ?? col?.key ?? '').toLowerCase();

        if (!normalizedKey && !normalizedLabel) {
            return false;
        }

        if (col?.displayFormat) {
            return col.displayFormat === 'amount';
        }

        const cacheKey = normalizedKey || normalizedLabel;
        if (!this.columnDisplayFormat.has(cacheKey)) {
            this.columnDisplayFormat.set(
                cacheKey,
                this.inferColumnDisplayFormat(col, normalizedLabel)
            );
        }

        return this.columnDisplayFormat.get(cacheKey) === 'amount';
    }

    private inferColumnDisplayFormat(
        col: { key: string; label?: string },
        labelLower: string
    ): 'text' | 'amount' {
        if (this.isIdentifierLikeLabel(labelLower)) {
            return 'text';
        }

        if (TableBuilderComponent.amountLikeTokens.some((token) => labelLower.includes(token))) {
            const hasNumericValue = (this.inputTableRowData || []).some(
                (row) => this.getNumericMeta(row?.[col.key]).isNumeric
            );
            return hasNumericValue ? 'amount' : 'text';
        }

        return 'text';
    }

    private isIdentifierLikeLabel(labelLower: string): boolean {
        return /\b(id|number|no|code|reference|ref|session)\b/.test(labelLower);
    }

    private refreshColumnDisplayFormat(): void {
        this.columnDisplayFormat.clear();
        (this.table_columns || []).forEach((col) => {
            this.isAmountColumn(col);
        });
    }

    onTargetSelectAttempt(row: any, args: any): void {
        if (this.allowDuplicateTargetSelection) {
            return;
        }
        if (args?.itemData?.isDisabled) {
            args.cancel = true;
            this.targetSelectionDuplicateClick.emit(row);
        }
    }

    onSourceSelectAttempt(row: any, args: any): void {
        if (this.allowDuplicateSourceSelection) {
            return;
        }
        if (args?.itemData?.isDisabled) {
            args.cancel = true;
            this.sourceSelectionDuplicateClick.emit(row);
        }
    }


    isTargetSelectionLocked(row: any): boolean {
        if (this.allowDuplicateTargetSelection) {
            return false;
        }
        if (row?.isNewRule) {
            return false;
        }
        const assignedCount = this.inputTableRowData.filter(
            (item) => !!item?.target
        ).length;
        return (
            this.targetOptions.length > 0 &&
            assignedCount >= this.targetOptions.length &&
            !row?.target
        );
    }

    onTargetBeforeOpen(row: any, args: any): void {
        if (row?.targetLocked) {
            if (args) {
                args.cancel = true;
            }
            return;
        }

        if (!this.isTargetSelectionLocked(row)) {
            return;
        }

        if (args) {
            args.cancel = true;
        }
        this.targetSelectionLockedClick.emit(row);
    }

    getTargetCssClass(row: any): string {
        const classes: string[] = [];
        if (row?.targetLocked) {
            classes.push('mapping-target-select--locked');
        }
        if (this.isTargetSelectionLocked(row)) {
            classes.push('mapping-target-select--locked');
        }
        if (row?.targetPrefilled) {
            classes.push('mapping-target-select--prefilled');
        }
        return classes.join(' ');
    }

    onSourceSelectChange(row: any, value: string): void {
        this.sourceSelectionChange.emit({ row, value: value ?? '' });
    }

    onMappingStatusSelectChange(row: any, value: string): void {
        this.mappingStatusSelectionChange.emit({ row, value: value ?? '' });
    }

    onSourceEditClick(row: any): void {
        this.sourceEditClick.emit(row);
    }

    onSourceEditConfirm(row: any): void {
        this.sourceEditConfirm.emit(row);
    }

    onSourceEditCancel(row: any): void {
        this.sourceEditCancel.emit(row);
    }

    onSourceEditHeaderClick(): void {
        this.sourceEditHeaderClick.emit();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            (changes.initialSelectedRowIds && !changes.initialSelectedRowIds.firstChange) ||
            (changes.inputTableRowData && !changes.inputTableRowData.firstChange)
        ) {
            this.syncInitialSelectedSnapshot();
        }

        if (changes.inputTableRowData && !changes.inputTableRowData.firstChange) {
            this.updateTableData();
            this.refreshColumnDisplayFormat();
            if (!this.showPagination) {
                this.mytableServices.pageSize = this.inputTableRowData.length || this.pageSize;
                this.mytableServices.page = 1;
            } else {
                this.mytableServices.page = this.pageNumber || 1;
            }
            this.syncSelectionFromInputs();
        }

        if (changes.table_columns && !changes.table_columns.firstChange) {
            this.refreshColumnDisplayFormat();
        }

        if (changes.selectedRowId && !changes.selectedRowId.firstChange) {
            this.syncSelectionFromInputs();
        }

        if (changes.pageSize && !changes.pageSize.firstChange) {
            this.mytableServices.pageSize = this.pageSize;
        }

        if (changes.pageNumber && !changes.pageNumber.firstChange) {
            this.mytableServices.page = this.pageNumber || 1;
        }

        if (changes.serverSidePagination && !changes.serverSidePagination.firstChange) {
            this.mytableServices.serverSidePagination = this.serverSidePagination;
        }

        if (changes.externalTotalItems && !changes.externalTotalItems.firstChange) {
            this.mytableServices.externalTotalItems = this.externalTotalItems;
        }

        if (changes.searchOperator && !changes.searchOperator.firstChange) {
            this.mytableServices.searchOperator = this.searchOperator;
        }

        if (changes.loading) {
            this.changeDetectorRef.markForCheck();
        }

        // if (changes.inputTableRowData) {
        //     console.log('cwr-table-builder: inputTableRowData changed:', this.inputTableRowData); // Supplier delete
        // }
    }

    private updateTableData() {
        this.mytableServices.updateTableData(this.inputTableRowData); // Update the service with new data
        // Mark for check to ensure OnPush picks up changes
        this.changeDetectorRef.markForCheck();
    }

    onSearchTermChange(searchTerm: string) {
        const nextSearchTerm = searchTerm ?? '';
        const currentSearchTerm = this.mytableServices.searchTerm;
        const wasSearching = currentSearchTerm.trim().length > 0;
        const willSearch = nextSearchTerm.trim().length > 0;

        if (!wasSearching && willSearch) {
            this.restorePageAfterSearch = this.mytableServices.page || 1;
            this.loggingService.debug('[TableBuilder] Search context captured.', {
                restorePageAfterSearch: this.restorePageAfterSearch,
            });
        }

        if (wasSearching && !willSearch) {
            const restorePage = this.restorePageAfterSearch || 1;
            this.mytableServices.applySearchState(nextSearchTerm, restorePage);
            this.loggingService.debug('[TableBuilder] Search clear restored page.', {
                restorePage,
            });
        } else {
            this.mytableServices.applySearchState(nextSearchTerm);
        }

        // Search changes only affect table state. Re-publishing the same raw
        // dataset here causes a second search cycle and can destabilize pager
        // restoration after clearing the filter.
        this.emitSelectedRows();
        this.changeDetectorRef.markForCheck();
        this.searchTermChange.emit(nextSearchTerm);
    }

    /** Handles pager page change and notifies parent for server-side fetch. */
    onPageNumberChanged(page: number): void {
        const isTransientEmptyDataReset =
            page === 1 &&
            this.pageNumber > 1 &&
            !this.serverSidePagination &&
            this.inputTableRowData.length === 0;

        if (isTransientEmptyDataReset) {
            this.loggingService.debug(
                '[TableBuilder] Ignored transient pager reset while data is still loading.',
                { requestedPage: page, externalPageNumber: this.pageNumber }
            );
            this.mytableServices.page = this.pageNumber;
            return;
        }

        this.mytableServices.page = page;
        this.loggingService.debug('[TableBuilder] Page changed.', { page });
        this.pageNumberChange.emit(page);
    }

    /** Handles page-size change and notifies parent for server-side fetch. */
    onPageSizeChanged(pageSize: number): void {
        this.mytableServices.pageSize = pageSize;
        this.mytableServices.page = 1;
        this.pageSizeChange.emit(pageSize);
        this.pageNumberChange.emit(1);
    }

    onSort({ column, direction }: SortEvent) {
        const normalizedDirection: 'asc' | 'desc' = direction === 'desc' ? 'desc' : 'asc';
        this.sortedColumn = column;
        this.sortedDirection = normalizedDirection;

        this.pageNumberChange.emit(1);

        if (this.serverSidePagination) {
            this.sortChange.emit({ column, direction: normalizedDirection });
            return;
        }

        const sortedData = [...this.inputTableRowData].sort((a, b) => {
            const res = a[column] < b[column] ? -1 : a[column] > b[column] ? 1 : 0;
            return normalizedDirection === 'asc' ? res : -res;
        });

        this.tableDataSubject.next(sortedData); // Update sorted data
        this.sortChange.emit({ column, direction: normalizedDirection });
    }

    toggleAll(event: any): void {
        const isChecked = this.resolveCheckboxChecked(event);
        this.mytableServices.currentPageRows$.pipe(take(1)).subscribe((pageRows) => {
            this.mytableServices.tableRowItemsRaw$.pipe(take(1)).subscribe((allRows) => {
                const selectedPageRowIds = new Set(
                    pageRows.map((row, index) => this.resolveRowIdentity(row, index))
                );

                const updatedData = allRows.map((row, index) => {
                    const rowId = this.resolveRowIdentity(row, index);
                    if (!selectedPageRowIds.has(rowId)) {
                        return row;
                    }

                    if (isChecked) {
                        return { ...row, selected: true };
                    }

                    if (this.preserveInitialSelectionsOnHeaderClear) {
                        return {
                            ...row,
                            selected: this.initialSelectedIdSet.has(String(row?.id ?? '')),
                        };
                    }

                    return { ...row, selected: false };
                });

                this.mytableServices.updateTableData(updatedData);
                this.emitSelectedRows();
                this.changeDetectorRef.markForCheck();
            });
        });
    }

    isAllSelected(): boolean {
        let allSelected = false;
        this.mytableServices.currentPageRows$.pipe(take(1)).subscribe((rows) => {
            const selectedRows = rows.filter((row) => row.selected);
            allSelected = selectedRows.length > 0 && selectedRows.length === rows.length;
        });
        return allSelected;
    }

    isAnySelected(): boolean {
        let anySelected = false;
        this.mytableServices.currentPageRows$.pipe(take(1)).subscribe((data) => {
            anySelected = data.some((row) => row.selected);
        });
        return anySelected;
    }

    onRowCheckboxChange(row: any, event?: any): void {
        const checked = this.resolveCheckboxChecked(event);
        const clickedRowId = this.resolveRowIdentity(row, -1);
        this.mytableServices.tableRowItemsRaw$.pipe(take(1)).subscribe((rawData) => {
            const updatedRows = rawData.map((item, index) => {
                const itemId = this.resolveRowIdentity(item, index);
                if (itemId !== clickedRowId) {
                    return item;
                }

                return {
                    ...item,
                    selected: event === undefined ? !item.selected : checked,
                };
            });

            this.mytableServices.updateTableData(updatedRows);
            // this.mytableServices.currentPageRows = updatedRows;
            this.emitSelectedRows();
            this.changeDetectorRef.markForCheck();
        });
    }

    onDownloadFileClick(row: any, event?: MouseEvent): void {
        const isDisabled =
            this.disableDownloadOperation ||
            (this.disableDownloadForRow ? this.disableDownloadForRow(row) : false);

        if (isDisabled) {
            event?.preventDefault();
            event?.stopPropagation();
            return;
        }

        if (this.downloadClickHandler) {
            this.downloadClickHandler(row);
            return;
        }
        this.downloadIconClicked.emit(row);
    }

    onViewFileClick(row: any, event?: MouseEvent): void {
        const isDisabled =
            this.disableViewOperation ||
            (this.disableViewForRow ? this.disableViewForRow(row) : false);

        if (isDisabled) {
            event?.preventDefault();
            event?.stopPropagation();
            return;
        }

        if (this.viewClickHandler) {
            this.viewClickHandler(row);
            return;
        }
        this.viewSingleItemClick.emit(row);
    }

    onOcrProcessClick(row: any): void {
        if (this.disableOcrForRow && this.disableOcrForRow(row)) {
            return;
        }

        if (this.ocrClickHandler) {
            this.ocrClickHandler(row);
            return;
        }
        this.ocrProcessClick.emit(row);
    }

    onDeleteFileClick(row: any): void {
        if (this.disableDeleteOperation || (this.disableDeleteForRow && this.disableDeleteForRow(row))) {
            return;
        }

        if (this.deleteClickHandler) {
            this.deleteClickHandler(row);
            return;
        }
        this.deleteRowClicked.emit(row);
    }

    onDeleteActionClick(row: any): void {
        this.deleteActionClick.emit(row);
    }

    onErpRuleChange(row: any, event?: any): void {
        const checked = this.resolveCheckboxChecked(event);
        row.erpRule = checked;
        this.erpRuleChange.emit({ row, checked });
        this.changeDetectorRef.markForCheck();
    }

    onNewRuleConfirmClick(row: any): void {
        this.newRuleConfirmClick.emit(row);
    }

    onEditCancelClick(row: any): void {
        this.editCancelClick.emit(row);
    }

    onReportFieldChange(row: any, value: string): void {
        row.reportFieldTouched = true;
        row.reportField = value;
        this.reportFieldChange.emit(row);
    }

    onToolFileClick(row: any): void {
        const isDisabled =
            this.disableToolOperation ||
            (this.disableToolForRow ? this.disableToolForRow(row) : false);

        if (isDisabled) {
            return;
        }

        if (this.toolClickHandler) {
            this.toolClickHandler(row);
            return;
        }
        this.toolSingleItemClick.emit(row);
    }

    private emitSelectedRows(): void {
        this.mytableServices.tableRowItemsRaw$.pipe(take(1)).subscribe((rows) => {
            // Filter the rows to get only the selected ones
            const selectedRows = rows.filter((row) => row.selected === true);

            // Emit the filtered selected rows
            this.selectedRowsChange.emit(selectedRows);
        });
    }

    onEditRowClick(row: any) {
        this.editRowClicked.emit(row);
    }

    /**
     * Reorders rows in the same sequence defined by drag-and-drop in the UI.
     *
     * Current behavior is intentionally limited to the fully visible dataset
     * (no paging/filtering) which matches the reconciliation mapping modal use
     * case. This avoids ambiguous reorder behavior on partial/filtered views.
     */
    onRowDrop(event: CdkDragDrop<any[]>): void {
        if (!this.enableRowReorder || this.loading) {
            return;
        }

        if (this.showPagination || !!this.mytableServices.searchTerm?.trim()) {
            return;
        }

        const previousIndex = event.previousIndex;
        const currentIndex = event.currentIndex;
        if (previousIndex === currentIndex) {
            return;
        }

        const rows = [...(this.inputTableRowData ?? [])];
        if (
            previousIndex < 0 ||
            currentIndex < 0 ||
            previousIndex >= rows.length ||
            currentIndex >= rows.length
        ) {
            return;
        }

        moveItemInArray(rows, previousIndex, currentIndex);
        this.inputTableRowData = rows;
        this.updateTableData();
        this.rowOrderChanged.emit(rows);
    }

    /**
     * Locks preview width to the exact row width for stable drag UX in fixed-width tables.
     */
    onRowDragStarted(event: CdkDragStart<any>): void {
        if (!this.lockDragPreviewWidth) {
            return;
        }
        const rowElement = event.source.getRootElement() as HTMLElement | null;
        if (!rowElement) {
            return;
        }

        const rowWidth = Math.round(rowElement.getBoundingClientRect().width);
        const tableElement = rowElement.closest('.datatable-table') as HTMLElement | null;
        const tableContainer = rowElement.closest('.datatable-container') as HTMLElement | null;
        const tableWidth = tableElement ? Math.round(tableElement.getBoundingClientRect().width) : 0;
        const containerWidth = tableContainer
            ? Math.round(tableContainer.getBoundingClientRect().width)
            : 0;
        // Preview must match visible table width (avoid stretched previews from scrollWidth).
        const lockedWidth = tableWidth || containerWidth || rowWidth || 0;
        if (!lockedWidth || Number.isNaN(lockedWidth)) {
            return;
        }

        // Lock the source row itself so CDK preview clone inherits stable width.
        this.dragSourceWidthBackup = rowElement.style.width || '';
        this.dragSourceMinWidthBackup = rowElement.style.minWidth || '';
        this.dragSourceMaxWidthBackup = rowElement.style.maxWidth || '';
        rowElement.style.width = `${lockedWidth}px`;
        rowElement.style.minWidth = `${lockedWidth}px`;
        rowElement.style.maxWidth = `${lockedWidth}px`;
        rowElement.style.boxSizing = 'border-box';

        const sourceCells = Array.from(rowElement.children).filter(
            (child) => (child as HTMLElement).tagName === 'TD'
        ) as HTMLElement[];
        const headerCells = Array.from(
            tableElement?.querySelectorAll('thead tr th') ?? []
        ) as HTMLElement[];
        const headerCellWidths = headerCells.map((cell) =>
            Math.round(cell.getBoundingClientRect().width)
        );
        const sourceCellWidths = sourceCells.map((cell) =>
            Math.round(cell.getBoundingClientRect().width)
        );
        const lockedCellWidths =
            headerCellWidths.length === sourceCellWidths.length
                ? headerCellWidths
                : sourceCellWidths;
        this.dragSourceCellStyleBackup = sourceCells.map((cell) => ({
            element: cell,
            width: cell.style.width || '',
            minWidth: cell.style.minWidth || '',
            maxWidth: cell.style.maxWidth || '',
            boxSizing: cell.style.boxSizing || '',
        }));
        sourceCells.forEach((cell, index) => {
            const cellWidth = lockedCellWidths[index] || 0;
            if (!cellWidth || Number.isNaN(cellWidth)) {
                return;
            }
            cell.style.width = `${cellWidth}px`;
            cell.style.minWidth = `${cellWidth}px`;
            cell.style.maxWidth = `${cellWidth}px`;
            cell.style.boxSizing = 'border-box';
        });

        const applyPreviewWidth = () => {
            const previews = Array.from(
                document.querySelectorAll('.cdk-drag-preview')
            ) as HTMLElement[];
            if (!previews.length) {
                return;
            }

            previews.forEach((preview) => {
                preview.style.setProperty('width', `${lockedWidth}px`, 'important');
                preview.style.setProperty('min-width', `${lockedWidth}px`, 'important');
                preview.style.setProperty('max-width', `${lockedWidth}px`, 'important');
                preview.style.setProperty('box-sizing', 'border-box', 'important');
                const previewRow =
                    preview.tagName === 'TR'
                        ? preview
                        : (preview.querySelector('tr') as HTMLElement | null);
                if (!previewRow) {
                    return;
                }
                previewRow.style.setProperty('width', `${lockedWidth}px`, 'important');
                previewRow.style.setProperty('min-width', `${lockedWidth}px`, 'important');
                previewRow.style.setProperty('max-width', `${lockedWidth}px`, 'important');
                previewRow.style.setProperty('box-sizing', 'border-box', 'important');
                previewRow.style.setProperty('display', 'table', 'important');
                previewRow.style.setProperty('table-layout', 'fixed', 'important');

                // Keep dragged row columns identical to source row column widths.
                const previewCells = Array.from(previewRow.children).filter(
                    (child) => (child as HTMLElement).tagName === 'TD'
                ) as HTMLElement[];
                previewCells.forEach((cell, index) => {
                    const cellWidth = lockedCellWidths[index] || 0;
                    if (!cellWidth || Number.isNaN(cellWidth)) {
                        return;
                    }
                    cell.style.setProperty('width', `${cellWidth}px`, 'important');
                    cell.style.setProperty('min-width', `${cellWidth}px`, 'important');
                    cell.style.setProperty('max-width', `${cellWidth}px`, 'important');
                    cell.style.setProperty('box-sizing', 'border-box', 'important');
                });
            });
        };

        applyPreviewWidth();
        requestAnimationFrame(applyPreviewWidth);
    }

    onRowDragEnded(_event: CdkDragEnd<any>): void {
        if (!this.lockDragPreviewWidth) {
            return;
        }
        const previews = Array.from(
            document.querySelectorAll('.cdk-drag-preview')
        ) as HTMLElement[];
        previews.forEach((preview) => {
            preview.style.removeProperty('width');
            preview.style.removeProperty('min-width');
            preview.style.removeProperty('max-width');
            preview.style.removeProperty('box-sizing');
        });

        const rowElement = _event.source.getRootElement() as HTMLElement | null;
        if (rowElement) {
            rowElement.style.width = this.dragSourceWidthBackup;
            rowElement.style.minWidth = this.dragSourceMinWidthBackup;
            rowElement.style.maxWidth = this.dragSourceMaxWidthBackup;
            rowElement.style.removeProperty('box-sizing');
        }
        this.dragSourceCellStyleBackup.forEach((backup) => {
            backup.element.style.width = backup.width;
            backup.element.style.minWidth = backup.minWidth;
            backup.element.style.maxWidth = backup.maxWidth;
            backup.element.style.boxSizing = backup.boxSizing;
        });
        this.dragSourceCellStyleBackup = [];
    }

    getColumnCount(): number {
        let count = this.table_columns?.length || 0;
        if (this.customCheckbox) count++;
        if (this.enableRowReorder) count++;
        if (this.iconOcrProcOperation) count++;
        if (this.iconViewOperation) count++;
        if (this.iconDownloadOperation) count++;
        if (this.iconEditOperation) count++;
        if (this.iconToolOperation) count++;
        if (this.iconDeleteOperation) count++;
        return count || 1; // Ensure at least 1 column for the message
    }

    private resolveCheckboxChecked(event: any): boolean {
        if (typeof event === 'boolean') {
            return event;
        }
        if (typeof event?.checked === 'boolean') {
            return event.checked;
        }
        if (typeof event?.event?.target?.checked === 'boolean') {
            return event.event.target.checked;
        }
        if (typeof event?.target?.checked === 'boolean') {
            return event.target.checked;
        }
        return false;
    }

    private resolveRowIdentity(row: any, fallbackIndex: number): string {
        if (row?.id !== null && row?.id !== undefined && String(row.id).trim() !== '') {
            return `id:${String(row.id)}`;
        }
        if (row?.filename) {
            return `filename:${String(row.filename)}`;
        }
        if (row?.fileName) {
            return `fileName:${String(row.fileName)}`;
        }
        if (row?.source) {
            return `source:${String(row.source)}`;
        }
        return `index:${fallbackIndex}`;
    }

    private syncInitialSelectedSnapshot(): void {
        if (!this.customCheckbox) {
            this.initialSelectedIdSet.clear();
            return;
        }

        if (Array.isArray(this.initialSelectedRowIds) && this.initialSelectedRowIds.length) {
            this.initialSelectedIdSet = new Set(
                this.initialSelectedRowIds.map((id) => String(id))
            );
            return;
        }

        this.initialSelectedIdSet = new Set(
            (this.inputTableRowData || [])
                .filter((row) => !!row?.selected)
                .map((row) => String(row?.id ?? ''))
        );
    }

    // Normalize session status to keep badge rendering stable.
    getNormalizedStatus(value: unknown): string {
        return normalizeSessionStatus(value);
    }

    // Resolve visual class for upload-session status values.
    getStatusBadgeClass(value: unknown): string {
        const normalized = this.getNormalizedStatus(value);
        if (normalized === SESSION_STATUS.OPEN) {
            return 'session-status-badge--open';
        }
        if (normalized === SESSION_STATUS.PROCESSING) {
            return 'session-status-badge--processing';
        }
        if (normalized === SESSION_STATUS.PROCESSED) {
            return 'session-status-badge--processed';
        }
        if (normalized === SESSION_STATUS.RECONCILED) {
            return 'session-status-badge--reconciled';
        }
        return 'session-status-badge--default';
    }
}
